/**
 * #2496 stage 3 — every derivation the **Access requests** section makes, out of the renderer.
 *
 * Sibling of `people-model.ts` and `availability-model.ts`, and here for the same reason: a
 * sentence assembled inline in JSX is a sentence no unit test can reach, and the three this section
 * builds are all ones a reader is asked to act on — a count in the nav chrome, a refusal in a
 * dialog, and an announcement a screen-reader user hears instead of watching a row disappear.
 *
 * ## ⛔ THE WRITE STATE IS **BESIDE** `PendingWrite`, NEVER INSIDE IT (D10)
 *
 * `PeoplePane`'s `PendingWrite` is keyed on a `PersonRow` and drives four roster writes through one
 * confirm dialog. An access request is not a `PersonRow` and never becomes one: D10 refuses a fifth
 * `PersonStage` and a third `PersonIdentity` member, because `PERSON_STAGES` is a closed tuple with
 * five exhaustive `Record<PersonStage, …>` maps over it and a `requested` stage would force a fifth
 * tone into a set `people-model.test.ts` pins as injective. ⇒ {@link AccessRequestWrite} is its own
 * union, keyed on a request id, and the two never merge.
 */
import type { AccessRequestStatus, AccessRequestView } from '../api/accessRequests.api';

/**
 * The two writes the section offers.
 *
 * ⛔ **REOPEN IS ABSENT, and its absence is D10 rather than an omission.** The section lists
 * `pending` only, so there is no denied row on screen to reopen — the verb ships on the API and on
 * `zion-ops`, where the operator can name a request by id. Adding it here would mean listing
 * terminal rows, which is a different screen.
 */
export const ACCESS_REQUEST_WRITE_KINDS = ['approve', 'deny'] as const;
export type AccessRequestWriteKind = (typeof ACCESS_REQUEST_WRITE_KINDS)[number];

/**
 * Which row has a write open, and how far it has got.
 *
 * ⛔ **`phase` IS A UNION, NOT A `busy: boolean` BESIDE THE ROW.** The two states it separates need
 * different controls: `confirming` shows a dialog the operator can cancel, `in-flight` shows one
 * that must not be cancelled or re-submitted while a privilege write is on the wire. A boolean
 * would admit `{ busy: true }` with no dialog open and `{ busy: false }` mid-request, and the
 * second of those is a double-submit.
 *
 * ⛔ **`requestId`, NOT the row.** The row is re-fetched after every write, so a held row object is
 * a snapshot that can disagree with the table under it. Holding the id means the dialog always
 * reads whatever the list currently says, and a row that vanished between opening and submitting
 * closes the dialog rather than confirming against a ghost.
 */
export type AccessRequestWrite = {
  readonly requestId: string;
  readonly kind: AccessRequestWriteKind;
  readonly phase: 'confirming' | 'in-flight';
} | null;

/** Is a write on the wire for this row right now? ⛔ Both halves — the id AND the phase. */
export function isWriteInFlight(write: AccessRequestWrite, requestId: string): boolean {
  return write !== null && write.requestId === requestId && write.phase === 'in-flight';
}

/**
 * The open write with its row attached, or `null` when no dialog should be on screen.
 *
 * ⛔ **THE RESOLUTION IS HERE RATHER THAN IN THE RENDERER, AND THE THIRD ARM IS WHY.** A dialog
 * needs the row — the address is its typed challenge and the seed for its display name — and
 * {@link AccessRequestWrite} holds an id, so somebody has to join them. Written inline in JSX that
 * join is `write !== null && row !== undefined`, whose *"a write is open and its row is gone"* case
 * no render can reach: the two move together in the component's own state. ⇒ the case exists, it is
 * simply unreachable from outside, and an unreachable arm in a renderer is a permanently uncovered
 * branch this package refuses to buy with a suppression.
 *
 * ⛔ **AND THE ARM IS REAL, not a formality.** The list re-reads after every write and on the
 * section's own refetch, so a request approved in another tab can leave under an open dialog. The
 * honest answer is to close the dialog: the alternative is a confirm panel challenging the operator
 * on an address that is no longer in the queue, which is the state D26's typed challenge exists to
 * prevent, arriving by a different route.
 */
export type OpenAccessRequestWrite = {
  readonly row: AccessRequestView;
  readonly kind: AccessRequestWriteKind;
  readonly phase: 'confirming' | 'in-flight';
} | null;

export function resolveAccessRequestWrite(
  write: AccessRequestWrite,
  rows: readonly AccessRequestView[],
): OpenAccessRequestWrite {
  if (write === null) return null;
  const row = rows.find((candidate) => candidate.id === write.requestId);
  if (row === undefined) return null;
  return { row, kind: write.kind, phase: write.phase };
}

/**
 * The rail count's hidden text, inflected.
 *
 * Sibling of `notOfferedLabel` in `availability-model.ts`, and here rather than at the call site for
 * the same reason it is: *"1 pending access requests"* is what makes a screen read as unowned.
 * `RailLink` renders the visible digit `aria-hidden` and this sentence in `sr-only`, so a screen
 * reader announces the number once, with its meaning.
 */
export function pendingAccessRequestsLabel(n: number): string {
  return `${n} pending access request${n === 1 ? '' : 's'}`;
}

/**
 * The three columns, as a closed key set.
 *
 * ⛔ **KEYS AND HEADERS HERE, CELLS IN THE PANE**, which is `people-model.ts`'s split: this module
 * is `.ts` and renders nothing, and the pane builds its `ScrollTableColumn[]` from an exhaustive
 * `Record<AccessRequestColumnKey, …>` cell map — so a fourth key fails the BUILD rather than
 * rendering a column with no header, and a header edited in one place cannot drift from the other.
 */
export const ACCESS_REQUEST_COLUMN_KEYS = ['requester', 'provider', 'requested'] as const;
export type AccessRequestColumnKey = (typeof ACCESS_REQUEST_COLUMN_KEYS)[number];

/**
 * ⛔ *"Requester"*, never *"Person"* or *"Operator"*. Nobody in this table is an operator yet —
 * that is the entire distinction between this section and the roster below it, and a column header
 * borrowed from the roster would erase it on the one screen where both are visible at once.
 */
export const ACCESS_REQUEST_COLUMN_HEADER: Readonly<Record<AccessRequestColumnKey, string>> = {
  requester: 'Requester',
  provider: 'Provider',
  requested: 'Requested',
};

/** ⛔ A Tailwind class, not a pixel number — `ScrollTableColumn.width`'s own rule. */
export const ACCESS_REQUEST_COLUMN_WIDTH: Readonly<Record<AccessRequestColumnKey, string>> = {
  requester: 'w-80',
  provider: 'w-32',
  requested: 'w-36',
};

/**
 * The starting value for the approve dialog's display-name field: the address's local part.
 *
 * ⛔ **DERIVED HERE BECAUSE THERE IS NOTHING STORED TO READ (D18).** `access_requests` keeps no
 * display name and no provider `name` claim, so the alternative to deriving one is an empty
 * required field on every approval. ⚠️ It is a STARTING value and the field is editable: nobody's
 * real name is their email local part, and the administrator is the one who knows.
 *
 * ⛔ **NO FALLBACK ARM, BECAUSE THERE IS NOTHING FOR ONE TO CATCH.** `String.prototype.split`
 * always yields at least one element, so `split('@')[0]` is `string` — an address with no `@`
 * simply comes back whole, which is the honest answer for a value the producer stores verbatim.
 *
 * ⚠️ An earlier version of this docblock justified an `indexOf` spelling by claiming
 * `noUncheckedIndexedAccess` types the index as `string | undefined`. **That flag is not set
 * anywhere in this repo's tsconfig chain** (measured), so the claim was false and the arm it
 * justified was dead code wearing a reason.
 */
export function displayNameFromEmail(email: string): string {
  return email.split('@')[0];
}

/**
 * What a screen reader is told after a row leaves the table.
 *
 * ⛔ **THE ADDRESS IS NAMED.** The visible feedback for an approval is a row disappearing from a
 * queue, which is no feedback at all to somebody who cannot see the table — and on a screen where
 * every row is a different person, *"Approved."* leaves the one question that matters unanswered.
 *
 * ⛔ **APPROVE SAYS WHAT ELSE HAPPENED.** Approving does not only clear the queue: it creates an
 * operator with the role the administrator picked. A sentence that reported only the queue change
 * would under-report a privilege write.
 *
 * ⛔ **THE STATUS THE PRODUCER ANSWERED DECIDES, NOT THE VERB.** An approve or a deny of a row whose
 * address already has an operator answers `superseded` (issue #4715): the producer closed the row
 * and wrote no operator and no role. *"Operator created."* would then report a privilege write that
 * did not happen, so that status gets its own sentence for both verbs.
 */
export function accessRequestOutcomeMessage(
  kind: AccessRequestWriteKind,
  email: string,
  status: AccessRequestStatus,
): string {
  if (status === 'superseded') return `${email} already has an operator. The request is closed.`;
  return kind === 'approve' ? `Approved ${email}. Operator created.` : `Denied ${email}.`;
}

/**
 * What the dialog says when a write comes back refused, and whether the list is now stale.
 *
 * ## ⛔⛔ KEYED ON THE STATUS CODE, AND NEVER ON THE MESSAGE TEXT
 *
 * Two of these codes carry an upstream sentence this console must not put on screen. The 403 is the
 * one that matters: `site-admin-api` throws `new ForbiddenException()` with no argument (D23) and
 * logs the producer's own text server-side under `proxy-upstream-403`, so the text reaching the
 * browser is the framework default — and a console that rendered whatever arrived would print
 * `Forbidden` at an administrator, or the producer's permission-slug sentence the day the proxy
 * stopped stripping it. The 400 is the same shape for a different reason: `assertSiteWideAssignable`
 * throws a message that runs five lines about resource-scoped roles, which is a producer-internal
 * explanation of a choice the operator made in a picker.
 *
 * ⇒ ⛔ **`apiErrorMessage(cause, …)` IS THE WRONG FUNCTION FOR THIS PATH** and is deliberately not
 * called here. It exists to surface a producer's sentence, which is right nearly everywhere and
 * wrong on exactly these two codes.
 *
 * ## ⚠️ 409 IS THE ONE ARM THAT DOES FORWARD
 *
 * A conflict is a statement about STATE — *"Access request is not pending"*, *"An operator with
 * this email already exists"* — and it is the sentence that tells the administrator which of the
 * two happened. It discloses nothing the row does not, and inventing a single sentence to cover
 * both would drop the distinction the operator needs.
 *
 * `refetch` is the second half: 404 and 409 both mean the list on screen disagrees with the server,
 * so the section re-reads when the dialog closes. A 403 and a 400 are facts about the CALLER and
 * the CHOICE, and the rows are still exactly what the server last said — re-reading there would
 * spend a request to re-render the same table.
 */
export interface AccessRequestFailure {
  readonly message: string;
  /** ⛔ `true` ⇒ the rows on screen are stale; the section re-reads when the dialog closes. */
  readonly refetch: boolean;
}

export function accessRequestFailure(
  status: number | null,
  upstreamMessage: string,
): AccessRequestFailure {
  if (status === 403) {
    /*
     * ⛔⛔ **VERB-NEUTRAL, BECAUSE ONE MAP SERVES BOTH WRITES AND BOTH CAN 403.** Approve carries the
     * service-level `cp:permission:grant` check, and D32 refuses approve, deny AND reopen to any
     * principal whose `sub` is not a uuid — so a deny answers 403 too. A sentence naming *approve*
     * would then tell an administrator their DENIAL failed for want of permission to APPROVE, which
     * sends them to ask for the wrong grant.
     *
     * ⚠️ *"decide"* is the queue's own verb for the pair, and it is the word D32 uses.
     */
    return {
      message: 'You don’t have permission to decide access requests.',
      refetch: false,
    };
  }
  if (status === 404) return { message: 'This request no longer exists.', refetch: true };
  if (status === 409) return { message: upstreamMessage, refetch: true };
  if (status === 400) return { message: 'That role can’t be granted here.', refetch: false };
  /*
   * ⛔ EVERYTHING ELSE GETS A FIXED SENTENCE, NOT THE UPSTREAM TEXT. The four codes above are the
   * whole refusal vocabulary the producer and the proxy declare; anything else is a fault rather
   * than a refusal, and the proxy turns an unmapped upstream failure into a 503 whose body is about
   * the proxy. ⚠️ `refetch: false` because a fault says nothing about whether the rows are stale,
   * and the section's own Refresh is the honest way to find out.
   */
  return { message: 'The change could not be applied.', refetch: false };
}

/** ⛔ Named, so the filter below and any later reader agree on which member of the union it is. */
const PENDING_STATUS: AccessRequestStatus = 'pending';

/**
 * The rows the section renders: `pending` only.
 *
 * ⛔ **FILTERED HERE AS WELL AS IN THE QUERY, AND THAT IS NOT BELT-AND-BRACES.** The read asks for
 * `?status=pending`, so a correct producer sends nothing else — but the list is re-read after every
 * write, and a request approved in another tab between the two reads is a terminal row arriving in
 * a table whose two controls are both refusals for it. ⚠️ D10's dedupe argument depends on this
 * exactly: one address can never appear both here and in the roster *because* this population is
 * `pending`, and a filter applied only at the query is a property of the server's behaviour rather
 * than of this screen's.
 */
export function pendingAccessRequests(
  rows: readonly AccessRequestView[],
): readonly AccessRequestView[] {
  return rows.filter((row) => row.status === PENDING_STATUS);
}
